/**
 * The "Start from" row on the model catalog: one button per preset, each with
 * the number of models it would show if clicked now.
 *
 * A sibling of `CostBandToggles` and `OriginToggle`, which are the same kind of
 * control: a row of pressable chips that write the page's one filter list. The
 * page owns the list; this only reports clicks.
 */
import type { CatalogPreset, CatalogPresetId } from './presetDefinitions'

export interface CatalogPresetsProps {
  presets: readonly CatalogPreset[]
  /** The preset the filter row currently equals, if any. */
  activeId: CatalogPresetId | null
  /** How many rows each preset would show if clicked now, with the origin
   * toggle and the name search applied. */
  counts: Readonly<Partial<Record<CatalogPresetId, number>>>
  onPick: (preset: CatalogPreset) => void
}

export function CatalogPresets({ presets, activeId, counts, onPick }: CatalogPresetsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="catalog-presets">
      <span className="text-sm">Start from</span>
      {presets.map(p => {
        const on = p.id === activeId
        return (
          <button
            key={p.id}
            type="button"
            aria-pressed={on}
            title={on ? `${p.hint} Click again to clear.` : p.hint}
            onClick={() => onPick(p)}
            className={[
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
              on
                ? 'bg-primary/10 text-primary border-primary/40'
                : 'bg-background text-muted-foreground hover:bg-muted',
            ].join(' ')}
          >
            <span>{p.label}</span>
            <span className="font-normal tabular-nums opacity-75">{counts[p.id] ?? 0}</span>
          </button>
        )
      })}
    </div>
  )
}
